import type { NavigateFunction, NavigateOptions, To } from "react-router-dom";
import { AuthService } from "@domain/services/auth.service";
import { CreditCardService } from "@domain/services/creditCard.service";
import { OrderService } from "@domain/services/order.service";
import { PromoService } from "@domain/services/promo.service";
import { SuggestionsStore } from "@infrastructure/stores/suggestions.store";
import { MachineService } from "@domain/services/machine.service";
import { MapService } from "@domain/services/map.service";

function toPathname(to: To): string {
  if (typeof to === "string") {
    return to.split("?")[0].split("#")[0];
  }

  return (to.pathname ?? "").split("?")[0].split("#")[0];
}

async function prefetchRouteChunk(pathname: string): Promise<void> {
  // dynamic routes
  if (pathname.startsWith("/order/")) {
    await import("@boundary/pages/11.order/order");
    return;
  }

  switch (pathname) {
    case "/":
      await import("@boundary/pages/1.splash/splashPage");
      return;
    case "/about":
      await import("@boundary/pages/2.about/about");
      return;
    case "/login":
      await import("@boundary/pages/3.login");
      return;
    case "/onboarding":
      await import("@boundary/pages/4.onboarding");
      return;
    case "/map":
      await import("@boundary/pages/5.map/map");
      return;
    case "/catalog":
      await import("@boundary/pages/6.catalog/catalog");
      return;
    case "/service":
      await import("@boundary/pages/7.service/service");
      return;
    case "/docview":
      await import("@boundary/pages/8.docview/docView");
      return;
    case "/cart":
      await import("@boundary/pages/9.cart/cart");
      return;
    case "/payment":
      await import("@boundary/pages/10.payment/payment");
      return;
    case "/payment/status":
      await import("@boundary/pages/10.payment/paymentStatus");
      return;
    case "/profile":
      await import("@boundary/pages/13.profile/profile");
      return;
    case "/order/history":
      await import("@boundary/pages/14.history/history");
      return;
    case "/discounts":
      await import("@boundary/pages/15.discounts/promos");
      return;
    case "/payment/settings":
      await import("@boundary/pages/16.paymentSettings/paymentMethods");
      return;
    case "/support":
      await import("@boundary/pages/17.support/support");
      return;
    case "/faq":
      await import("@boundary/pages/18.faq/faq");
      return;
    case "/menu":
      await import("@boundary/pages/12.menu/menu");
      return;
    default:
      return;
  }
}

async function prefetchRouteData(pathname: string): Promise<void> {
  switch (pathname) {
    case "/profile":
    case "/menu":
      await AuthService.refreshUser();
      return;
    case "/order/history":
      await OrderService.fetchOrderHistory();
      return;
    case "/discounts":
      await PromoService.loadPromoList();
      return;
    case "/payment/settings":
    case "/payment":
      // Предзагружаем карты для быстрой работы страницы оплаты
      await CreditCardService.fetchCards();
      return;
    case "/cart":
      // Предзагружаем карты на странице корзины, чтобы при переходе на payment все было готово
      await CreditCardService.fetchCards();
      return;
    case "/catalog":
      await SuggestionsStore.load();
      return;
    case "/map":
      // Предзагружаем машины и центрируем карту заранее
      await MachineService.init();
      // Ждем загрузки машин (максимум 5 секунд)
      let attempts = 0;
      const maxAttempts = 50; // 50 * 100ms = 5 секунд максимум
      while (MachineService.getMachines().length === 0 && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      // Если машины загружены, центрируем карту
      if (MachineService.getMachines().length > 0) {
        await MapService.initializeLocation();
      }
      return;
    case "/onboarding":
      // Предзагружаем картинки onboarding при переходе на страницу
      const { preloadOnboardingImages } = await import("@boundary/pages/4.onboarding");
      preloadOnboardingImages();
      return;
    default:
      return;
  }
}

export function prefetchRoute(to: To): void {
  const pathname = toPathname(to);
  if (!pathname.startsWith("/")) return;

  void Promise.allSettled([
    prefetchRouteChunk(pathname),
    prefetchRouteData(pathname)
  ]);
}

export function prefetchAndNavigate(
  navigate: NavigateFunction,
  to: To,
  options?: NavigateOptions
): void {
  prefetchRoute(to);
  navigate(to, options);
}


